import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import Axios from 'axios';
import _ from 'underscore';
import Cards from 'design-components/Cards';
import { url } from 'utils/url';
import './MediaSelectModal.css';

const MediaSelectModal = ({
  visible,
  onCancel,
  onSelect,
  siteId,
  initialSelected,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSelected(initialSelected || []);
    setSearch('');
    if (!siteId) return;

    const load = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
        const [pub, unpub] = await Promise.all([
          Axios.post(
            `${url}/inventory/filter`,
            { siteId, type: 'published' },
            { headers }
          ),
          Axios.post(
            `${url}/inventory/filter`,
            { siteId, type: 'unpublished' },
            { headers }
          ),
        ]);
        const combined = [
          ...(pub.data?.data || []),
          ...(unpub.data?.data || []),
        ];
        const seen = new Set();
        const dedup = combined.filter((m) => {
          if (!m || !m._id || seen.has(m._id)) return false;
          seen.add(m._id);
          return true;
        });
        setItems(dedup);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [visible, siteId, initialSelected]);

  const filtered = useMemo(() => {
    const arr = (items || []).filter(Boolean);
    if (!search) return arr;
    const q = search.toLowerCase();
    return arr.filter((m) => (m.name || '').toLowerCase().includes(q));
  }, [items, search]);

  const isSelected = (id) => selected.includes(id);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={960}
      title="Inventory"
      className="media-select-modal"
      destroyOnClose
    >
      <div className="msm-search">
        <i className="feather-search" />
        <input
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="msm-list">
        {loading && <div className="msm-empty">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="msm-empty">No media</div>
        )}
        {!loading && _.chunk(filtered, 2).map((chunk, idx) => (
          <div className="msm-row" key={idx}>
            {chunk.map((media) => (
              <div
                key={media._id}
                className={`msm-card ${isSelected(media._id) ? 'selected' : ''}`}
                onClick={() => toggle(media._id)}
              >
                {isSelected(media._id) && (
                  <div className="msm-overlay">
                    <div className="msm-check">
                      <i className="feather-check" />
                    </div>
                  </div>
                )}
                <Cards
                  data={media}
                  type="media"
                  isShowMedia
                  isEdit={false}
                  isDelete={false}
                />
              </div>
            ))}
            {chunk.length < 2 && <div className="msm-card-empty" />}
          </div>
        ))}
      </div>

      <div className="msm-footer">
        <button type="button" className="msm-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="msm-select"
          onClick={() =>
            onSelect(
              selected,
              items.filter((m) => selected.includes(m._id))
            )
          }
        >
          Select
        </button>
      </div>
    </Modal>
  );
};

export default MediaSelectModal;